import { lift } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import type { NodeType, ResolvedPos, Schema } from 'prosemirror-model';
import { NodeRange } from 'prosemirror-model';
import type { EditorState, Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

import type { BlockquoteSchema } from './blockquote-node';
import type { EditorButton, EditorMode, RtPMPlugin } from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY } from '../../helpers/utils';
import { NodeName } from '../../schema/nodes';

export class BlockquoteService {
  constructor(
    protected nodeType: NodeType,
    protected schema: Schema,
  ) {}

  public isActive(state: EditorState) {
    // isActive => everything selected has a blockquote among ancestors
    const { doc } = state;
    const { $from, $to } = state.selection;

    let cursor = $to.pos;
    while (cursor >= $from.pos) {
      const parentQuotePos = this.getAncestorQuotePos(doc.resolve(cursor));
      if (parentQuotePos === null) {
        return false;
      }
      cursor = parentQuotePos - 1;
    }
    return true;
  }

  public toggle(
    state: EditorState,
    dispatch: (tr: Transaction) => void,
    origin: HTMLElement,
    isContextMenu: boolean,
  ) {
    if (!this.isActive(state)) {
      const tr = this.wrapParts(state);
      return tr ? dispatch(tr) : false;
    } else {
      return lift(state, dispatch);
    }
  }

  private wrapParts(state: EditorState): Transaction | null {
    const { doc } = state;
    const { $from, $to } = state.selection;
    const end = $to.after();
    const start = $from.before();
    let cursor = end;
    const ranges: NodeRange[] = [];
    let rangeEnd: ResolvedPos | null = null;

    // finding ranges which don't have blockquotes as ancestors
    while (cursor >= start) {
      const resolved = doc.resolve(cursor);
      const parentQuotePos = this.getAncestorQuotePos(resolved);
      if (parentQuotePos === null) {
        if (!rangeEnd) {
          rangeEnd = resolved;
        }
        cursor = cursor - 1;
      } else {
        if (rangeEnd) {
          ranges.push(new NodeRange(doc.resolve(cursor + 1), rangeEnd, rangeEnd.depth));
          rangeEnd = null;
        }
        cursor = parentQuotePos;
      }
    }

    // when starting node is not inside the blockquotes, we are left with unclosed range
    if (rangeEnd) {
      ranges.push(new NodeRange(doc.resolve(start), rangeEnd, rangeEnd.depth));
    }

    let tr: Transaction | null = null;
    ranges.forEach(range => {
      tr = tr
        ? tr.wrap(range, [{ type: this.nodeType }])
        : state.tr.wrap(range, [{ type: this.nodeType }]);
    });
    return tr;
  }

  private getAncestorQuotePos(pos$: ResolvedPos) {
    let depth = pos$.depth;
    while (depth > 0) {
      const node = pos$.node(depth);
      if (node.type.name === NodeName.Blockquote) {
        return pos$.before(depth);
      }
      depth = depth - 1;
    }
    return null;
  }
}

export class BlockquotePlugin implements RtPMPlugin {
  public api: {
    blockquoteService: BlockquoteService;
  };

  public buttons = {
    toggleBlockquote: <EditorButton>{
      tooltipText: 'wysiwyg.quote',
      icon: 'format-quote',
      isActive: state => this.api.blockquoteService.isActive(state),
      run: (...args) => {
        this.api.blockquoteService.toggle(...args);
        this.editorView.focus();
      },
    },
  };

  private editorView: EditorView;

  public beforeInit(mode: EditorMode, schema: BlockquoteSchema) {
    this.api = {
      blockquoteService: new BlockquoteService(schema.nodes.blockquote, schema),
    };
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: BlockquoteSchema) {
    return [
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings(schema)) },
    ];
  }

  private getKeyBindings(schema: BlockquoteSchema) {
    return {};
  }
}
